import { type ReactNode } from 'react'
import { t } from '@/lib/i18n'

export const dKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/**
 * 稿子的 `.bars`：柱状趋势，**柱子可点**，范围可切。
 *
 * 用 flex + div 而不是 SVG：SVG 里做可点区要自己算坐标，而 flex-1 的按钮天然
 * 撑满可用宽度、天然能挂 :active。
 *
 * ⚠️ 可点尺寸的一处已知张力：30 天档位下每根柱子只有约 11px 宽（375px 屏），
 * 低于 44px 的下限。缓解而不是无视：柱子是**整列 112px 高**的按钮（纵向足够），
 * 而且分段控件给了 7 天档（约 48px/柱）。真要点准某一天，切到 7 天。
 */
export function BarChart({ daily, days = 30, selected = null, onSelect }: {
  daily: Record<string, number>
  days?: number
  /** 选中那天的 YYYY-MM-DD；null = 未选 */
  selected?: string | null
  /** 不给就是只读图（公开分享页用）：柱子渲染成 span 而不是按钮 ——
      看起来能点却点不动，比不能点更糟。 */
  onSelect?: (key: string) => void
}) {
  const rows: { k: string; v: number }[] = []
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i)
    const k = dKey(d)
    rows.push({ k, v: daily[k] || 0 })
  }
  // 下限 10：全是 0 的那几天不该让唯一一根 1 分的柱子顶到天花板。
  const max = Math.max(10, ...rows.map((x) => x.v))

  return (
    <div className="flex items-end gap-0.5 h-28" role="group" aria-label={t('dashboard.trend')}>
      {rows.map((x) => {
        const bar = (
          <span
            className={
              'w-full rounded-t transition-colors ' +
              (x.k === selected ? 'bg-gradient-to-b from-brand to-brand2' : x.v > 0 ? 'bg-tint' : 'bg-sunk')
            }
            style={{ height: x.v > 0 ? `${Math.max(4, (x.v / max) * 100)}%` : '3px' }}
          />
        )
        const cls = 'flex-1 basis-0 min-w-0 h-full flex items-end rounded-t'
        const label = `${x.k}: ${t('charts.points', { count: x.v })}`

        return onSelect ? (
          <button
            key={x.k}
            type="button"
            onClick={() => onSelect(x.k)}
            aria-pressed={x.k === selected}
            aria-label={label}
            className={cls}
          >
            {bar}
          </button>
        ) : (
          <span key={x.k} title={label} className={cls}>{bar}</span>
        )
      })}
    </div>
  )
}

/* 热力图是**单色序列**，不是分类调色：四阶全部从 --color-green 派生，
   低两阶用 color-mix 混入卡片底色。深色下 --color-card 会跟着翻，
   所以整条梯度自动成立，不用另写一套深色值。
   格子和图例共用这一个数组 —— 原先图例是另外手挑的两个 hex，改一边忘一边就对不上。 */
const HEAT = [
  'var(--color-line)',                                              // 0：这天没打卡
  'color-mix(in srgb, var(--color-green) 28%, var(--color-card))',
  'color-mix(in srgb, var(--color-green) 55%, var(--color-card))',
  'var(--color-green)',
  'var(--color-green-strong)',                                      // 4：最热
]
const level = (v: number, max: number) => {
  if (!v) return 0
  const t = v / max
  return t > 0.75 ? 4 : t > 0.5 ? 3 : t > 0.25 ? 2 : 1
}

/**
 * 稿子的 `.heat`：13 列 × 7 行，`1fr` + `aspect-ratio:1`，**任何屏宽都不溢出**。
 *
 * 原来是固定像素的 SVG 包在 overflow-x:auto 里 —— 手机上要横滑，
 * 而热力图的全部价值就是「一眼看一片」，需要横滑等于看不到那一片。
 *
 * 颜色仍是上面那套从 --color-green 派生的单色序列，没有换成稿子的四档蓝：
 * token 表里 --brand（蓝）的语义是「可点 / 已选 / 强调」，热力图格子三样都不是，
 * 而 --color-green 的语义正是「已完成」。要严格照稿是加四对 token × 两套主题的事。
 */
export function Heatmap({ daily }: { daily: Record<string, number> }) {
  const weeks = 13
  // 让最右一列是「包含今天的那一周」，且每列自上而下是周一…周日。
  const end = new Date(); end.setDate(end.getDate() + (6 - ((end.getDay() + 6) % 7)))
  const max = Math.max(1, ...Object.values(daily))

  const cells: ReactNode[] = []
  for (let row = 0; row < 7; row++) {
    // 只标周一 / 周三 / 周五三行，七行全标会挤。
    cells.push(
      <span key={`l${row}`} className="text-[9px] text-faint text-right pr-0.5 leading-none">
        {row === 0 || row === 2 || row === 4 ? t(`dashboard.weekday_${row}`) : ''}
      </span>,
    )
    for (let w = weeks - 1; w >= 0; w--) {
      const dt = new Date(end); dt.setDate(end.getDate() - w * 7 + (row - 6))
      const k = dKey(dt); const v = daily[k] || 0
      cells.push(
        <span
          key={`${w}-${row}`}
          title={`${k}: ${t('charts.points', { count: v })}`}
          className="aspect-square rounded-[3px] block"
          style={{ background: HEAT[level(v, max)] }}
        />,
      )
    }
  }

  return (
    <div>
      <div className="grid gap-[3px] items-center" style={{ gridTemplateColumns: `18px repeat(${weeks}, 1fr)` }}>
        {cells}
      </div>
      <div className="flex items-center gap-1.5 mt-2.5 text-[11px] text-faint">
        {t('charts.less')}
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i} className="block w-[11px] h-[11px] rounded-[3px]" style={{ background: HEAT[i] }} />
        ))}
        {t('charts.more')}
      </div>
    </div>
  )
}
